import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/site/PageHeader";
import { SiteFooter } from "@/components/site/FinalCTA";
import AuthDialog from "@/components/site/AuthDialog";
import { useAuth } from "@/context/AuthContext";
import { portraitsApi } from "@/lib/cardsApi";
import { formatApiError } from "@/lib/api";
import { PORTRAITS } from "@/constants/testIds";

function errMsg(e) {
  return formatApiError(e?.response?.data?.detail) || e?.message || "Something went wrong.";
}

export default function MyPortraitsPage() {
  const { user, ready } = useAuth();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!ready || !user) return;
    (async () => {
      try {
        const data = await portraitsApi.list();
        setItems(data.portraits);
      } catch (e) { toast.error(errMsg(e)); }
    })();
  }, [ready, user]);

  async function handleUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Please keep the image under 8 MB.");
      return;
    }
    setBusy(true);
    try {
      const doc = await portraitsApi.upload(file);
      setItems((cur) => [doc, ...cur]);
      toast.success("Portrait uploaded.");
    } catch (e2) {
      toast.error(errMsg(e2));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this portrait? It will be removed from anywhere it's used.")) return;
    try {
      await portraitsApi.remove(id);
      setItems((cur) => cur.filter((p) => p.id !== id));
      toast.success("Portrait deleted.");
    } catch (e) { toast.error(errMsg(e)); }
  }

  if (!ready) return <div className="min-h-screen bg-ivory" />;

  if (!user) {
    return (
      <div data-testid={PORTRAITS.page} className="min-h-screen bg-ivory text-ink">
        <PageHeader
          eyebrow="My Portraits"
          title="Your family, in"
          italic="portrait"
          tail="form."
          subtitle="Upload photographs of the people in your archive. Use them as portraits in your family tree, or as the centrepiece of a personal message card."
        />
        <div className="max-w-[900px] mx-auto px-6 md:px-10 py-20">
          <div data-testid={PORTRAITS.loginPrompt} className="rounded-2xl border border-ivory-300 bg-white/60 p-10 text-center">
            <p className="font-serif text-2xl text-ink">Sign in to upload portraits.</p>
            <p className="mt-2 text-ink-50">Your uploaded photos are private to you.</p>
            <button
              onClick={() => setAuthOpen(true)}
              className="mt-8 rounded-full bg-saffron text-ivory px-6 py-3 font-medium hover:bg-saffron-deep transition"
            >
              Sign in or register
            </button>
          </div>
        </div>
        <AuthDialog open={authOpen} onOpenChange={setAuthOpen} initialTab="register" />
        <SiteFooter />
      </div>
    );
  }

  return (
    <div data-testid={PORTRAITS.page} className="min-h-screen bg-ivory text-ink">
      <PageHeader
        eyebrow="My Portraits"
        title="Your family, in"
        italic="portrait"
        tail="form."
        subtitle="Upload photographs of the people in your archive. Use them as portraits in your family tree, or as the centrepiece of a personal message card. Everything here is private to you."
      />

      <div className="max-w-[1200px] mx-auto px-6 md:px-10 pt-10 pb-24">
        {/* Upload bar */}
        <div className="flex flex-wrap items-center gap-3 border-b border-ivory-300 pb-6">
          <input
            data-testid={PORTRAITS.uploadInput}
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleUpload}
            className="hidden"
          />
          <button
            data-testid={PORTRAITS.uploadBtn}
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            className="h-10 rounded-full bg-saffron text-ivory px-5 text-sm font-medium hover:bg-saffron-deep disabled:opacity-60 transition"
          >
            {busy ? "Uploading…" : "+ Upload a portrait"}
          </button>
          <span className="text-xs text-ink-50">JPEG, PNG or WebP. Up to 8 MB. Resized to 1800px on the long edge.</span>
          <div className="ml-auto text-xs text-ink-50">
            <Link to="/cards" className="text-tulsi hover:text-saffron transition ink-link">
              Create a card with a portrait →
            </Link>
          </div>
        </div>

        {items.length === 0 ? (
          <div className="mt-16 rounded-2xl border border-ivory-300 bg-white/60 p-14 text-center">
            <p className="font-serif text-3xl text-ink">No portraits yet.</p>
            <p className="mt-2 text-ink-50 max-w-md mx-auto">
              Start with a portrait of yourself, or a photograph of a grandparent you'd like to add to your family tree.
            </p>
          </div>
        ) : (
          <div
            data-testid={PORTRAITS.grid}
            className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6"
          >
            {items.map((p) => (
              <motion.div
                key={p.id}
                data-testid={PORTRAITS.card(p.id)}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="group relative rounded-2xl overflow-hidden border border-ivory-300 bg-white/70"
              >
                <div className="aspect-[3/4] overflow-hidden">
                  <img
                    src={portraitsApi.publicUrl(p.url)}
                    alt={p.original_name}
                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
                  />
                </div>
                <div className="px-3 py-2 text-xs text-ink-50 truncate">
                  {p.original_name}
                </div>
                <button
                  data-testid={PORTRAITS.deleteBtn(p.id)}
                  onClick={() => handleDelete(p.id)}
                  className="absolute top-2 right-2 h-8 w-8 rounded-full bg-black/60 text-ivory opacity-0 group-hover:opacity-100 transition"
                  aria-label="Delete portrait"
                  title="Delete"
                >
                  ×
                </button>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <SiteFooter />
    </div>
  );
}
